/**
 * The institution's books.
 *
 * Every figure here is summed from the ledger journal rather than read from a
 * stored balance. It costs a little on each load and it means any total can be
 * explained by the rows underneath it — which is the only useful property a
 * financial screen has.
 *
 * Two sources feed the journal and they are shown separately because they are
 * earned differently: consultations, which come from the institution's doctors,
 * and services, which the institution sells directly.
 */

import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Icon,
  PageHeader,
  Panel,
  Spinner,
  StatCard,
  DateInput,
} from '../../components/common';
import { useAsync } from '../../hooks/useAsync';
import { institutionApi } from '../../api/endpoints';
import { formatCurrency } from '../../domain/pricing';
import { isoToDisplayDate, todayKey, addDays } from '../../domain/dates';
import { SERVICE_CATEGORY_LABELS } from '../../domain/institutions';
import { timeRange } from '../../domain/bidi';

const SOURCE_LABEL = {
  consultation: 'كشوفات ومتابعات',
  service: 'خدمات',
  adjustment: 'تسويات',
};

const CATEGORY_LABEL = { ...SERVICE_CATEGORY_LABELS, consultation: 'كشوفات ومتابعات' };

/** Presets, because "this month" is the question nine times out of ten. */
const RANGES = [
  { id: 'all', label: 'كل الفترات' },
  { id: 'month', label: 'آخر 30 يوماً' },
  { id: 'week', label: 'آخر 7 أيام' },
];

export function InstitutionFinance() {
  const [range, setRange] = useState('month');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const window = useMemo(() => {
    if (from && to) return { from, to };
    if (range === 'month') return { from: addDays(todayKey(), -30), to: todayKey() };
    if (range === 'week') return { from: addDays(todayKey(), -7), to: todayKey() };
    return {};
  }, [range, from, to]);

  const state = useAsync(
    () => institutionApi.finance(window),
    [window.from, window.to],
    { initialData: null },
  );

  const members = useAsync(() => institutionApi.members(), [], { initialData: [] });

  const nameFor = useMemo(() => {
    const map = new Map();
    for (const member of members.data || []) {
      map.set(String(member.userId), member.user?.name || `حساب #${member.userId}`);
    }
    return (id) => map.get(String(id)) || `حساب #${id}`;
  }, [members.data]);

  if (state.error) return <ErrorState error={state.error} onRetry={state.reload} />;
  if (state.loading && !state.data) return <Spinner label="جاري تحميل الحسابات..." />;

  const totals = state.data?.totals || { income: 0, refunds: 0, net: 0, entries: 0 };
  const byCategory = state.data?.byCategory || {};
  const byMember = state.data?.byMember || {};
  const entries = state.data?.entries || [];

  const categoryRows = Object.entries(byCategory).sort(([, a], [, b]) => b - a);
  const memberRows = Object.entries(byMember).sort(([, a], [, b]) => b - a);
  const biggest = categoryRows[0]?.[1] || 1;

  return (
    <div className="space-y-6">
      <PageHeader
        title="الحسابات والإيرادات"
        subtitle={window.from ? timeRange(isoToDisplayDate(window.from), isoToDisplayDate(window.to), '—') : 'كل الفترات'}
        icon="banknote"
        actions={
          <Button variant="ghost" icon="refresh-cw" onClick={state.reload} loading={state.loading}>
            تحديث
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon="trending-up" label="إجمالي الوارد" value={formatCurrency(totals.income)} accent="emerald" />
        <StatCard icon="rotate-ccw" label="المرتجعات" value={formatCurrency(totals.refunds)} accent="amber" />
        <StatCard icon="banknote" label="الصافي" value={formatCurrency(totals.net)} accent="cyan" />
        <StatCard icon="list" label="عدد الحركات" value={totals.entries} accent="purple" />
      </div>

      {/* Range */}
      <Panel>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap gap-1.5">
            {RANGES.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => {
                  setRange(option.id);
                  setFrom('');
                  setTo('');
                }}
                className={`rounded-lg border px-3 py-1.5 text-xs font-bold transition-colors ${
                  range === option.id && !from
                    ? 'border-cyan-500 bg-cyan-500/15 text-cyan-300'
                    : 'border-white/10 bg-slate-900 text-slate-400 hover:text-slate-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="flex flex-1 flex-wrap items-center gap-2">
            <DateInput
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              className="sm:w-40"
            />
            <span className="text-xs text-slate-500">إلى</span>
            <DateInput
              value={to}
              onChange={(event) => setTo(event.target.value)}
              className="sm:w-40"
            />
          </div>
        </div>
      </Panel>

      {totals.entries === 0 ? (
        <Panel>
          <EmptyState
            icon="banknote"
            title="لا توجد حركات في هذه الفترة"
            message="تُسجَّل الإيرادات عند إنهاء الكشف أو الخدمة، وليس عند الحجز."
          />
        </Panel>
      ) : (
        <>
          <div className="grid gap-4 lg:grid-cols-2">
            {/* Where the money came from */}
            <Panel>
              <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-white">
                <Icon name="pie-chart" size={18} className="text-cyan-400" />
                الإيراد حسب النوع
              </h3>

              <div className="space-y-3">
                {categoryRows.map(([category, amount]) => (
                  <div key={category}>
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <span className="text-xs text-slate-300">
                        {CATEGORY_LABEL[category] || category}
                      </span>
                      <span className="text-xs font-bold text-emerald-400">
                        {formatCurrency(amount)}
                      </span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-slate-800">
                      <div
                        className="h-full rounded-full bg-gradient-to-l from-emerald-500 to-cyan-500"
                        style={{ width: `${Math.max(2, (amount / biggest) * 100)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </Panel>

            {/* Who generated it */}
            <Panel>
              <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-white">
                <Icon name="users" size={18} className="text-purple-400" />
                الإيراد حسب الطبيب
              </h3>

              {memberRows.length === 0 ? (
                <EmptyState
                  icon="users"
                  title="لا يوجد إيراد من الكشوفات بعد"
                  message="تظهر هنا إيرادات أطباء المنشأة بعد إنهاء الكشوفات."
                />
              ) : (
                <ul className="space-y-2">
                  {memberRows.map(([userId, amount]) => (
                    <li
                      key={userId}
                      className="flex items-center gap-3 rounded-xl border border-white/[0.07] bg-white/[0.04] p-2.5"
                    >
                      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-purple-500/10 text-purple-300">
                        <Icon name="stethoscope" size={15} />
                      </span>
                      <span className="min-w-0 flex-1 truncate text-xs text-slate-300">
                        {nameFor(userId)}
                      </span>
                      <span className="shrink-0 text-xs font-bold text-emerald-400">
                        {formatCurrency(amount)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          </div>

          {/* The journal itself */}
          <Panel>
            <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-white">
              <Icon name="list" size={18} className="text-emerald-400" />
              كشف الحركات
            </h3>

            <div className="custom-scrollbar max-h-[28rem] space-y-2 overflow-y-auto">
              {entries.map((entry) => {
                const refund = entry.direction === 'refund';
                return (
                  <div
                    key={entry.id}
                    className="flex flex-wrap items-center gap-3 rounded-xl border border-white/[0.07] bg-white/[0.04] p-3"
                  >
                    <span
                      className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg ${
                        refund ? 'bg-amber-500/10 text-amber-300' : 'bg-emerald-500/10 text-emerald-300'
                      }`}
                    >
                      <Icon name={refund ? 'rotate-ccw' : 'trending-up'} size={16} />
                    </span>

                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-bold text-white">
                        {entry.note || SOURCE_LABEL[entry.sourceType] || entry.sourceType}
                      </p>
                      <p className="truncate text-[11px] text-slate-400">
                        {isoToDisplayDate(entry.occurredOn)} —{' '}
                        {CATEGORY_LABEL[entry.category] || SOURCE_LABEL[entry.sourceType]}
                        {entry.memberUserId ? ` — ${nameFor(entry.memberUserId)}` : ''}
                      </p>
                    </div>

                    <Badge className="border-white/10 bg-white/5 text-slate-300">
                      {SOURCE_LABEL[entry.sourceType] || entry.sourceType}
                    </Badge>

                    <span
                      className={`shrink-0 text-sm font-bold ${
                        refund ? 'text-amber-400' : 'text-emerald-400'
                      }`}
                    >
                      {refund ? '-' : '+'} {formatCurrency(entry.amount)}
                    </span>
                  </div>
                );
              })}
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}

export default InstitutionFinance;
